import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  ChevronRight, 
  CheckCircle2, 
  Search, 
  FileText,
  Settings,
  Award,
  Tag,
  Layers,
  Target
} from 'lucide-react';

const FlipkartListingSEO = () => {
  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Flipkart Product Listing & SEO Services | Expert Cataloging</title>
        <meta name="description" content="Professional Flipkart product listing with SEO-optimized titles, descriptions & images. Rank higher in Flipkart search. Trusted listing service provider." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/flipkart-product-listing-seo" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/flipkart-account-management-services" className="text-slate-700 hover:text-primary font-medium">Flipkart</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Product Listing & SEO</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.03) 0%, rgba(6, 182, 212, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Flipkart Product Listing & <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">SEO Optimization</span>
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">
                Professional Listing Services to Boost Your Flipkart Visibility
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8">
                Amazing Sales provides professional Flipkart product listing and SEO optimization services to help sellers improve product visibility and increase sales on Flipkart marketplace. Our expert team creates optimized listings that rank higher in search results and convert more visitors into buyers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?service=flipkart-listing">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free Listing Audit
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=flipkart-listing">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to Listing Expert
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-400/20 to-cyan-400/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-white rounded-2xl shadow-2xl p-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">+85%</div>
                      <div className="text-sm text-slate-600">Search Visibility</div>
                    </div>
                    <div className="bg-cyan-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-cyan-600">+48%</div>
                      <div className="text-sm text-slate-600">Conversion Rate</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">100%</div>
                      <div className="text-sm text-slate-600">SEO Optimized</div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-purple-600">Fast</div>
                      <div className="text-sm text-slate-600">Turnaround</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Why Product Listing Optimization Matters
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              On Flipkart, your product listing is your storefront. A well-optimized listing doesn't just attract more visitors—it converts them into customers. Amazing Sales specializes in creating and optimizing Flipkart product listings that rank higher in search results, showcase your products effectively, and drive more sales.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our team understands Flipkart's search algorithm and buyer behavior, allowing us to craft listings that not only meet marketplace requirements but exceed customer expectations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Brand Approval Support Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-blue-100"
          >
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Award className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-slate-900">
                  Brand Approval Support
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  If sellers want to list branded products on Flipkart, brand approval may be required. Our team helps sellers obtain brand approval through Flipkart support so that products can be listed successfully.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-orange-50 rounded-2xl p-6 border-l-4 border-orange-500">
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                      For Traders / Resellers
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      A <strong className="text-orange-600">brand invoice</strong> will be required to verify authorization to sell the brand's products. This proves you have a legitimate supply chain for the branded items.
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-2xl p-6 border-l-4 border-green-500">
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                      For Brand Owners
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      A <strong className="text-green-600">trademark certificate</strong> will be required to verify brand ownership. This establishes your legal rights to the brand on the platform.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Our Brand Approval Process:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      'Document collection & verification',
                      'Application preparation',
                      'Submission to Flipkart',
                      'Follow-up with support team',
                      'Approval tracking',
                      'Post-approval listing setup'
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-blue-500" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* New Product Listings Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <FileText className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-slate-900">
                New Product Listings
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                We create new product listings with SEO-friendly product titles, detailed descriptions, optimized keywords, and proper product attributes to improve search visibility on Flipkart.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Each listing is carefully crafted to maximize visibility in Flipkart's search results while providing customers with all the information they need to make a purchase decision.
              </p>
              
              <div className="space-y-4">
                {[
                  { title: 'SEO-Friendly Titles', desc: 'Keyword-rich titles that rank higher in search' },
                  { title: 'Detailed Descriptions', desc: 'Comprehensive product information that converts' },
                  { title: 'Optimized Keywords', desc: 'Strategic keyword placement for maximum visibility' },
                  { title: 'Proper Attributes', desc: 'Accurate product specifications and details' },
                  { title: 'Category Placement', desc: 'Correct categorization for better discoverability' },
                  { title: 'Image Guidelines', desc: 'Recommendations for high-quality product images' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-slate-900">{item.title}:</span>
                      <span className="text-slate-600 ml-1">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 border border-green-100"
            >
              <h3 className="font-heading text-2xl font-bold mb-6 text-slate-900">
                What We Deliver
              </h3>
              <div className="space-y-4">
                {[
                  'Complete product listing creation',
                  'Keyword research and implementation',
                  'Competitor analysis for positioning',
                  'Category and subcategory mapping',
                  'Backend search term optimization',
                  'Quality compliance check',
                  'Listing review and refinement'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-3 bg-white rounded-lg p-3 shadow-sm">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-bold text-sm">{idx + 1}</span>
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Existing Listing Optimization Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl p-8 border border-purple-100"
            >
              <h3 className="font-heading text-2xl font-bold mb-6 text-slate-900">
                Our Optimization Process
              </h3>
              <div className="space-y-4">
                {[
                  { step: 'Audit', desc: 'Analyze current listing performance' },
                  { step: 'Research', desc: 'Identify keyword opportunities' },
                  { step: 'Optimize', desc: 'Update content and attributes' },
                  { step: 'Enhance', desc: 'Improve descriptions and titles' },
                  { step: 'Monitor', desc: 'Track ranking improvements' },
                  { step: 'Refine', desc: 'Continuous optimization' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-4 bg-white rounded-lg p-4 shadow-sm">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{idx + 1}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">{item.step}:</span>
                      <span className="text-slate-600 ml-1">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <Settings className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-slate-900">
                Existing Listing Optimization
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                If listings already exist, we optimize them by updating product content, improving keywords, updating attributes, and improving SEO-friendly product descriptions for better visibility.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Our optimization service transforms underperforming listings into high-converting product pages that attract more customers and drive more sales.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  'Content updates',
                  'Keyword improvement',
                  'Attribute updates',
                  'SEO enhancement',
                  'Title optimization',
                  'Description refresh',
                  'Image recommendations',
                  'Competitor benchmarking'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-purple-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Amazing Sales */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Why Choose Amazing Sales for Flipkart Listings?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Search, title: 'SEO Expertise', desc: 'Deep understanding of Flipkart search algorithm', color: 'blue' },
              { icon: Target, title: 'Conversion Focus', desc: 'Listings designed to convert visitors to buyers', color: 'green' },
              { icon: Layers, title: 'Complete Service', desc: 'From brand approval to listing optimization', color: 'purple' },
              { icon: Tag, title: 'Competitive Pricing', desc: 'Affordable services with proven ROI', color: 'orange' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-slate-50 p-6 rounded-xl border border-slate-200 text-center hover:shadow-lg transition-shadow"
              >
                <div className={`w-14 h-14 bg-${item.color}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <item.icon className={`w-7 h-7 text-${item.color}-600`} />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-600 to-cyan-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Ready to Optimize Your Flipkart Listings?
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Let Amazing Sales create high-converting product listings that rank higher and sell more. Get started with a free listing audit today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?service=flipkart-listing">
                <Button className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-7 rounded-full text-lg font-semibold shadow-lg">
                  Get Free Listing Audit
                  <ArrowRight className="ml-2" size={24} />
                </Button>
              </Link>
              <Link to="/flipkart-account-management-services">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-7 rounded-full text-lg font-semibold">
                  View All Flipkart Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FlipkartListingSEO;
